import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, Upload, Users, FileText, Loader2, CheckCircle, XCircle, Clock, RefreshCw, MessageSquare, ListFilter, LayoutTemplate, Workflow, Plus, Check, AlertTriangle, ArrowRight, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../services/api';

const DRAFT_KEY = 'bulk_message_draft_state';

const getInitialDraft = () => {
  try {
    const saved = sessionStorage.getItem(DRAFT_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (e) {
    return {};
  }
};

function BulkMessage() {
  const navigate = useNavigate();
  const location = useLocation();
  const draft = getInitialDraft();

  const [inputMode, setInputMode] = useState(draft.inputMode || 'text');
  const [columnType, setColumnType] = useState(draft.columnType || 'none');
  const [numbersText, setNumbersText] = useState(draft.numbersText || '');
  const [namesText, setNamesText] = useState(draft.namesText || '');
  const [file, setFile] = useState(null);

  const [processedContacts, setProcessedContacts] = useState(draft.processedContacts || []);
  const [confirmedRecipients, setConfirmedRecipients] = useState(draft.confirmedRecipients || null);
  const [lastColumnType, setLastColumnType] = useState(draft.lastColumnType || 'none');
  const [processing, setProcessing] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(draft.selectedTemplateId || '');
  const [sendMode, setSendMode] = useState(draft.sendMode || 'text');
  const [waTemplates, setWaTemplates] = useState([]);
  const [eligibleFlows, setEligibleFlows] = useState([]);
  const [selectedCampaignFlowId, setSelectedCampaignFlowId] = useState(draft.selectedCampaignFlowId || '');
  const [settingCampaignFlow, setSettingCampaignFlow] = useState(false);
  const [selectedWaTemplateId, setSelectedWaTemplateId] = useState(draft.selectedWaTemplateId || '');
  const [variableMapping, setVariableMapping] = useState(draft.variableMapping || []);
  const [messageContent, setMessageContent] = useState(draft.messageContent || '');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const [sending, setSending] = useState(false);
  const [activeCampaignId, setActiveCampaignId] = useState(null);
  const [campaignData, setCampaignData] = useState(null);

  const [pastCampaigns, setPastCampaigns] = useState([]);
  const [loadingPast, setLoadingPast] = useState(true);
  const [showApiSetupModal, setShowApiSetupModal] = useState(false);

  const pollRef = useRef(null);
  const fileInputRef = useRef(null);
  const isResettingRef = useRef(false);

  // Auto-save form draft so navigation to FlowBuilder doesn't lose state
  useEffect(() => {
    if (isResettingRef.current) return;

    const hasAnyContent =
      (confirmedRecipients && confirmedRecipients.length > 0) ||
      (processedContacts && processedContacts.length > 0) ||
      numbersText.trim().length > 0 ||
      messageContent.trim().length > 0;

    if (hasAnyContent) {
      const stateToSave = {
        inputMode,
        columnType,
        numbersText,
        namesText,
        processedContacts,
        confirmedRecipients,
        lastColumnType,
        selectedTemplateId,
        sendMode,
        selectedCampaignFlowId,
        selectedWaTemplateId,
        variableMapping,
        messageContent
      };
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(stateToSave));
    }
  }, [
    inputMode,
    columnType,
    numbersText,
    namesText,
    processedContacts,
    confirmedRecipients,
    lastColumnType,
    selectedTemplateId,
    sendMode,
    selectedCampaignFlowId,
    selectedWaTemplateId,
    variableMapping,
    messageContent
  ]);

  useEffect(() => {
    loadTemplates();
    loadWaTemplates();
    loadPastCampaigns();
    loadEligibleFlows();
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    if (location.state?.confirmedRecipients) {
      setConfirmedRecipients(location.state.confirmedRecipients);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const loadTemplates = async () => {
    try {
      const res = await API.get('/campaigns/templates');
      setTemplates(res.data.data || []);
    } catch (error) {
      console.error('Load templates error:', error);
    }
  };

  const loadWaTemplates = async () => {
    try {
      const res = await API.get('/templates');
      setWaTemplates((res.data.data || []).filter(t => t.status === 'APPROVED'));
    } catch (error) {
      console.error('Load WhatsApp templates error:', error);
    }
  };

  const loadEligibleFlows = async () => {
    try {
      const res = await API.get('/flows');
      const allFlows = res.data.data || [];
      setEligibleFlows(allFlows.filter(f => !f.is_published));

      const current = allFlows.find(f => f.is_campaign_flow);
      if (current && !selectedCampaignFlowId) {
        setSelectedCampaignFlowId(String(current.id));
      }
    } catch (error) {
      console.error('Load flows error:', error);
    }
  };

  const handleSelectCampaignFlow = async (id) => {
    if (!id) return;
    setSettingCampaignFlow(true);
    const toastId = toast.loading('Setting campaign flow...');
    try {
      await API.patch(`/flows/${id}/set-campaign`);
      setSelectedCampaignFlowId(id);
      toast.success('Flow set for campaign replies', { id: toastId });
      loadEligibleFlows();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to set campaign flow', { id: toastId });
    } finally {
      setSettingCampaignFlow(false);
    }
  };

  const handleCreateNewFlow = () => navigate('/flow-builder');

  const selectedWaTemplate = waTemplates.find(t => String(t.id) === String(selectedWaTemplateId));

  const handleWaTemplateSelect = (id) => {
    setSelectedWaTemplateId(id);
    const t = waTemplates.find(t => String(t.id) === String(id));
    const count = t?.variable_count || 0;
    setVariableMapping(Array.from({ length: count }, () => ({ source: 'category', fallback: 'Friend' })));
  };

  const updateVariableMapping = (index, field, value) => {
    setVariableMapping(prev => prev.map((v, i) => i === index ? { ...v, [field]: value } : v));
  };

  const loadPastCampaigns = async () => {
    try {
      setLoadingPast(true);
      const res = await API.get('/campaigns');
      setPastCampaigns(res.data.data || []);
    } catch (error) {
      console.error('Load campaigns error:', error);
    } finally {
      setLoadingPast(false);
    }
  };

  const handleTemplateSelect = (id) => {
    setSelectedTemplateId(id);
    const t = templates.find(t => String(t.id) === String(id));
    if (t) setMessageContent(t.body);
  };

  const handleProcessNumbers = async () => {
    if (inputMode === 'text' && !numbersText.trim()) {
      toast.error('Please enter at least one number');
      return;
    }
    if (inputMode === 'text' && columnType === 'name' && !namesText.trim()) {
      toast.error('Please enter names (one per line, matching the numbers)');
      return;
    }
    if (inputMode === 'file' && !file) {
      toast.error('Please select a file');
      return;
    }

    const toastId = toast.loading('Processing numbers...');
    setProcessing(true);
    try {
      let res;
      if (inputMode === 'file') {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('columnType', columnType);
        res = await API.post('/campaigns/upload-numbers', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        res = await API.post('/campaigns/upload-numbers', {
          numbers: numbersText,
          columnType,
          names: columnType === 'name' ? namesText : undefined
        });
      }

      const contacts = res.data.data || [];
      setProcessedContacts(contacts);
      setLastColumnType(columnType);
      setConfirmedRecipients(null);
      toast.success(res.data.message, { id: toastId });

      if (contacts.length > 0) {
        navigate('/bulk-message/organize', {
          state: {
            contacts,
            columnType
          }
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process numbers', { id: toastId });
    } finally {
      setProcessing(false);
    }
  };

  const handleOrganizeNumbers = () => {
    navigate('/bulk-message/organize', { state: { contacts: processedContacts, columnType: lastColumnType } });
  };

  const handleEditRecipients = () => {
    navigate('/bulk-message/organize', { state: { contacts: confirmedRecipients, columnType: lastColumnType } });
  };

  // ✅ Manual Clear / Reset Process Handler
  const handleClearProcess = () => {
    if (!window.confirm('Are you sure you want to clear current numbers & message draft?')) return;
    
    isResettingRef.current = true;
    sessionStorage.removeItem(DRAFT_KEY);

    setProcessedContacts([]);
    setConfirmedRecipients(null);
    setNumbersText('');
    setNamesText('');
    setMessageContent('');
    setFile(null);
    setSelectedTemplateId('');
    setSelectedWaTemplateId('');
    setVariableMapping([]);
    if (fileInputRef.current) fileInputRef.current.value = '';

    toast.success('Campaign draft cleared!');
    setTimeout(() => {
      isResettingRef.current = false;
    }, 200);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || !messageContent.trim()) {
      toast.error('Template name and message are required');
      return;
    }
    const toastId = toast.loading('Saving template...');
    try {
      await API.post('/campaigns/templates', { name: templateName, body: messageContent, variables: [] });
      toast.success('Template saved!', { id: toastId });
      setTemplateName('');
      setSaveAsTemplate(false);
      loadTemplates();
    } catch (error) {
      toast.error('Failed to save template', { id: toastId });
    }
  };

  const activeRecipients = confirmedRecipients || processedContacts;

  const handleSendNow = async () => {
    if (activeRecipients.length === 0) {
      toast.error('Process numbers first');
      return;
    }
    if (sendMode === 'text' && !messageContent.trim()) {
      toast.error('Message content is required');
      return;
    }
    if (sendMode === 'template' && !selectedWaTemplateId) {
      toast.error('Please select a WhatsApp template');
      return;
    }
    if (sendMode === 'template') {
      const missingFixed = variableMapping.some(v => v.source === 'fixed' && !v.fallback?.trim());
      if (missingFixed) {
        toast.error('Please fill in all fixed-text variables');
        return;
      }
    }

    const toastId = toast.loading('Starting campaign...');
    setSending(true);
    try {
      const payload = sendMode === 'template'
        ? {
          name: `Campaign ${new Date().toLocaleString('en-IN')}`,
          isTemplate: true,
          whatsappTemplateName: selectedWaTemplate.template_name,
          templateLanguage: selectedWaTemplate.language_code,
          variableMapping,
          recipients: activeRecipients
        }
        : {
          name: `Campaign ${new Date().toLocaleString('en-IN')}`,
          templateId: selectedTemplateId || null,
          messageContent,
          recipients: activeRecipients
        };

      const res = await API.post('/campaigns', payload);
      const campaignId = res.data.data.campaignId;
      toast.success('Campaign started! Sending in progress...', { id: toastId });
      setActiveCampaignId(campaignId);
      startPolling(campaignId);

      // ✅ Flush state completely on successful campaign dispatch
      isResettingRef.current = true;
      sessionStorage.removeItem(DRAFT_KEY);

      setProcessedContacts([]);
      setConfirmedRecipients(null);
      setNumbersText('');
      setNamesText('');
      setMessageContent('');
      setFile(null);
      setSelectedWaTemplateId('');
      setSelectedTemplateId('');
      setVariableMapping([]);
      if (fileInputRef.current) fileInputRef.current.value = '';

      setTimeout(() => {
        isResettingRef.current = false;
      }, 500);

    } catch (error) {
      if (error.response?.data?.errorCode === 'WHATSAPP_NOT_CONFIGURED') {
        toast.dismiss(toastId);
        setShowApiSetupModal(true);
      } else {
        toast.error(error.response?.data?.message || 'Failed to start campaign', { id: toastId });
      }
    } finally {
      setSending(false);
    }
  };

  const startPolling = (campaignId) => {
    clearInterval(pollRef.current);
    fetchCampaignStatus(campaignId);
    pollRef.current = setInterval(async () => {
      const data = await fetchCampaignStatus(campaignId);
      if (data && data.campaign.status !== 'processing') {
        clearInterval(pollRef.current);
        loadPastCampaigns();
      }
    }, 3000);
  };

  const fetchCampaignStatus = async (campaignId) => {
    try {
      const res = await API.get(`/campaigns/${campaignId}/status`);
      setCampaignData(res.data.data);
      return res.data.data;
    } catch (error) {
      return null;
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: { bg: '#F1F5F9', color: '#64748B', icon: <Clock size={12} />, label: 'Pending' },
      sent: { bg: '#DBEAFE', color: '#1E40AF', icon: <CheckCircle size={12} />, label: 'Sent' },
      delivered: { bg: '#DCFCE7', color: '#166534', icon: <CheckCircle size={12} />, label: 'Delivered' },
      read: { bg: '#EDE9FE', color: '#6D28D9', icon: <CheckCircle size={12} />, label: 'Read' },
      failed: { bg: '#FEE2E2', color: '#991B1B', icon: <XCircle size={12} />, label: 'Failed' },
      draft: { bg: '#F1F5F9', color: '#64748B', icon: <Clock size={12} />, label: 'Draft' },
      processing: { bg: '#FEF3C7', color: '#92400E', icon: <Loader2 size={12} className="bm-spin" />, label: 'Processing' },
      completed: { bg: '#DCFCE7', color: '#166534', icon: <CheckCircle size={12} />, label: 'Completed' },
    };
    const s = map[status] || map.pending;
    return (
      <span className="bm-chip" style={{ background: s.bg, color: s.color }}>
        {s.icon} {s.label}
      </span>
    );
  };

  const step1Done = activeRecipients.length > 0;
  const step2Done = (sendMode === 'text' && messageContent.trim().length > 0) ||
                    (sendMode === 'template' && !!selectedWaTemplateId);

  const hasDraftActive = activeRecipients.length > 0 || messageContent.trim().length > 0 || numbersText.trim().length > 0;

  return (
    <div className="dash-page">
      <div className="bm-shell">
        <style>{`
          .bm-shell { max-width: 100%; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #0F172A; }
          .bm-spin { animation: bm-spin 1s linear infinite; }
          @keyframes bm-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

          .bm-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; margin-bottom: 26px; flex-wrap: wrap; }
          .bm-eyebrow { display: flex; align-items: center; gap: 8px; color: #14803C; font-size: 12.5px; font-weight: 700; margin-bottom: 8px; }
          .bm-eyebrow-dot { width: 7px; height: 7px; border-radius: 50%; background: #25D366; }
          .bm-title { font-size: 25px; font-weight: 700; color: #0F172A; margin: 0 0 6px 0; letter-spacing: -0.3px; }
          .bm-subtitle { font-size: 14px; color: #64748B; margin: 0; max-width: 460px; line-height: 1.5; }

          .bm-tracker { display: flex; align-items: center; background: #FFFFFF; border: 1px solid #E2E8F0; border-radius: 14px; padding: 18px 22px; margin-bottom: 22px; }
          .bm-tstep { display: flex; align-items: center; gap: 10px; }
          .bm-tnum { width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12.5px; font-weight: 700; flex-shrink: 0; }
          .bm-tnum.done { background: #25D366; color: #fff; }
          .bm-tnum.current { background: #1E3A5F; color: #fff; }
          .bm-tnum.pending { background: #F1F5F9; color: #94A3B8; }
          .bm-tlabel { font-size: 13px; font-weight: 600; color: #334155; white-space: nowrap; }
          .bm-tlabel.pending-label { color: #94A3B8; }
          .bm-tline { flex: 1; height: 2px; background: #E2E8F0; margin: 0 12px; border-radius: 2px; overflow: hidden; }
          .bm-tline-fill { height: 100%; background: #25D366; width: 0%; transition: width 0.3s ease; }

          .bm-card { background: #FFFFFF; border: 1px solid #E2E8F0; border-radius: 14px; padding: 24px; margin-bottom: 18px; }
          .bm-card.optional { background: #FAFBFC; }
          .bm-card-head { display: flex; align-items: flex-start; gap: 13px; margin-bottom: 20px; }
          .bm-icon-badge { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; background: #EFF6FF; color: #1E3A5F; }
          .bm-icon-badge.green { background: #EDFCF4; color: #14803C; }
          .bm-card-title { font-size: 16px; font-weight: 700; color: #0F172A; margin: 0 0 3px 0; display: flex; align-items: center; }
          .bm-card-desc { font-size: 13.5px; color: #64748B; margin: 0; line-height: 1.5; }
          .bm-optional-tag { display: inline-block; font-size: 10.5px; font-weight: 700; color: #94A3B8; background: #F1F5F9; padding: 2px 8px; border-radius: 6px; margin-left: 8px; }

          .bm-toggle-group { display: flex; gap: 6px; margin-bottom: 20px; background: #F8FAFC; padding: 5px; border-radius: 10px; width: fit-content; flex-wrap: wrap; }
          .bm-toggle { display: flex; align-items: center; gap: 7px; padding: 9px 16px; border-radius: 8px; border: none; background: transparent; color: #475569; font-size: 13.5px; font-weight: 600; cursor: pointer; }
          .bm-toggle.active { background: #1E3A5F; color: #fff; }

          .bm-field { margin-bottom: 16px; }
          .bm-label { display: block; font-size: 13px; font-weight: 600; color: #334155; margin-bottom: 7px; }
          .bm-required { color: #DC2626; }
          .bm-input, .bm-textarea, .bm-select { width: 100%; border: 1px solid #E2E8F0; border-radius: 9px; padding: 10px 13px; font-size: 14px; color: #0F172A; background: #fff; font-family: inherit; box-sizing: border-box; }
          .bm-textarea { resize: vertical; line-height: 1.5; }
          .bm-input:focus, .bm-textarea:focus, .bm-select:focus { outline: none; border-color: #1E3A5F; box-shadow: 0 0 0 3px rgba(30,58,95,0.08); }
          .bm-hint { font-size: 12.5px; color: #94A3B8; margin: 7px 0 0 0; line-height: 1.4; }

          .bm-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 11px 20px; border-radius: 9px; font-size: 14px; font-weight: 600; border: none; cursor: pointer; white-space: nowrap; }
          .bm-btn:disabled { cursor: not-allowed; opacity: 0.5; }
          .bm-btn-primary { background: #25D366; color: #fff; }
          .bm-btn-primary:not(:disabled):hover { background: #1FB855; }
          .bm-btn-dark { background: #1E3A5F; color: #fff; }
          .bm-btn-dark:not(:disabled):hover { background: #16293F; }
          .bm-btn-outline { background: #fff; color: #334155; border: 1px solid #E2E8F0; }
          .bm-btn-outline:not(:disabled):hover { background: #F8FAFC; border-color: #CBD5E1; }
          .bm-btn-sm { padding: 7px 14px; font-size: 13px; }
          .bm-btn-block { width: 100%; padding: 13px 20px; font-size: 15px; }

          .bm-clear-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 8px; background: #FEF2F2; color: #DC2626; border: 1px solid #FECACA; font-size: 12.5px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
          .bm-clear-btn:hover { background: #FEE2E2; }

          .bm-check-row { display: flex; align-items: center; gap: 9px; margin-bottom: 4px; }
          .bm-check-row label { font-size: 13.5px; color: #475569; cursor: pointer; }

          .bm-notice { border-radius: 9px; padding: 11px 14px; font-size: 12.5px; margin-top: 14px; line-height: 1.5; }
          .bm-notice-amber { background: #FFFBEB; border: 1px solid #FDE68A; color: #92400E; }
          .bm-notice-green { background: #F0FDF9; border: 1px solid rgba(37,211,102,0.25); color: #065F46; }

          .bm-recipients-summary { margin-top: 16px; padding: 14px 16px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
          .bm-recipients-count { font-size: 14px; color: #0F172A; }
          .bm-recipients-count strong { color: #14803C; }

          .bm-table-wrap { border: 1px solid #E2E8F0; border-radius: 10px; overflow: hidden; overflow-x: auto; }
          .bm-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
          .bm-table thead { background: #F8FAFC; }
          .bm-table th { text-align: left; padding: 11px 16px; font-weight: 600; color: #475569; font-size: 12.5px; border-bottom: 1px solid #E2E8F0; white-space: nowrap; }
          .bm-table td { padding: 11px 16px; border-bottom: 1px solid #F1F5F9; color: #334155; }
          .bm-table tbody tr:last-child td { border-bottom: none; }
          .bm-table tbody tr:hover { background: #FAFBFC; }

          .bm-chip { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 20px; font-size: 11.5px; font-weight: 700; }

          .bm-empty { text-align: center; padding: 40px 20px; }
          .bm-empty-icon { width: 52px; height: 52px; border-radius: 14px; background: #F1F5F9; display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; color: #94A3B8; }
          .bm-empty-title { font-size: 15px; font-weight: 700; color: #334155; margin: 0 0 5px 0; }
          .bm-empty-desc { font-size: 13px; color: #94A3B8; margin: 0; }

          .bm-var-row { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
          .bm-preview-box { padding: 13px 15px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px; margin-bottom: 16px; font-size: 13.5px; color: #475569; white-space: pre-wrap; line-height: 1.5; }

          .bm-section-title { font-size: 16px; font-weight: 700; color: #0F172A; margin: 0; display: flex; align-items: center; gap: 8px; }
          .bm-flow-active { padding: 11px 14px; background: #F0FDF9; border: 1px solid rgba(37,211,102,0.25); border-radius: 9px; font-size: 12.5px; color: #065F46; display: flex; align-items: center; gap: 8px; margin-top: 14px; }

          @media (max-width: 640px) {
            .bm-tracker { flex-wrap: wrap; gap: 10px; }
            .bm-tline { display: none; }
            .bm-header { flex-direction: column; }
          }
        `}</style>

        {/* Header with Clear Process button */}
        <div className="bm-header">
          <div>
            <div className="bm-eyebrow"><span className="bm-eyebrow-dot" /> WhatsApp Campaigns</div>
            <h1 className="bm-title">Bulk Message</h1>
            <p className="bm-subtitle">Reach multiple contacts at once — upload numbers, compose your message, and send.</p>
          </div>

          {/* ✅ CLEAR / RESET CAMPAIGN DRAFT BUTTON */}
          {hasDraftActive && (
            <button
              onClick={handleClearProcess}
              className="bm-clear-btn"
              type="button"
              title="Clear draft & start a fresh campaign"
            >
              <RotateCcw size={14} /> Clear / New Campaign
            </button>
          )}
        </div>

        {/* Step tracker */}
        <div className="bm-tracker">
          <div className="bm-tstep">
            <div className={`bm-tnum ${step1Done ? 'done' : 'current'}`}>{step1Done ? <Check size={14} /> : '1'}</div>
            <span className={`bm-tlabel ${!step1Done ? '' : ''}`}>Recipients</span>
          </div>
          <div className="bm-tline"><div className="bm-tline-fill" style={{ width: step1Done ? '100%' : '0%' }} /></div>
          <div className="bm-tstep">
            <div className={`bm-tnum ${step2Done ? 'done' : step1Done ? 'current' : 'pending'}`}>{step2Done ? <Check size={14} /> : '2'}</div>
            <span className={`bm-tlabel ${!step1Done ? 'pending-label' : ''}`}>Message</span>
          </div>
          <div className="bm-tline"><div className="bm-tline-fill" style={{ width: step2Done ? '100%' : '0%' }} /></div>
          <div className="bm-tstep">
            <div className={`bm-tnum ${step1Done && step2Done ? 'current' : 'pending'}`}>3</div>
            <span className={`bm-tlabel ${!(step1Done && step2Done) ? 'pending-label' : ''}`}>Send</span>
          </div>
        </div>

        {/* Step 1 — Recipients */}
        <div className="bm-card">
          <div className="bm-card-head">
            <div className="bm-icon-badge"><Users size={18} /></div>
            <div>
              <p className="bm-card-title">Add recipients</p>
              <p className="bm-card-desc">Type numbers manually or upload an Excel/CSV file.</p>
            </div>
          </div>

          <div className="bm-toggle-group">
            <button onClick={() => { setInputMode('text'); setColumnType('none'); }} className={`bm-toggle ${inputMode === 'text' ? 'active' : ''}`} type="button">Type numbers</button>
            <button onClick={() => { setInputMode('file'); setColumnType('none'); }} className={`bm-toggle ${inputMode === 'file' ? 'active' : ''}`} type="button"><Upload size={14} /> Upload file</button>
          </div>

          <div className="bm-field">
            <label className="bm-label">What does the second column contain?</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={() => setColumnType('none')} className={`bm-toggle ${columnType === 'none' ? 'active' : ''}`} type="button" style={{ background: columnType === 'none' ? '#1E3A5F' : '#F8FAFC' }}>Only number</button>
              <button onClick={() => setColumnType('name')} className={`bm-toggle ${columnType === 'name' ? 'active' : ''}`} type="button" style={{ background: columnType === 'name' ? '#1E3A5F' : '#F8FAFC' }}>Number + name</button>
              {inputMode === 'file' && (
                <button onClick={() => setColumnType('category')} className={`bm-toggle ${columnType === 'category' ? 'active' : ''}`} type="button" style={{ background: columnType === 'category' ? '#1E3A5F' : '#F8FAFC' }}>Number + category</button>
              )}
            </div>
            {inputMode === 'text' && <p className="bm-hint">Category-based upload is only available via Excel/CSV.</p>}
          </div>

          {inputMode === 'text' ? (
            <>
              <div className="bm-field">
                <label className="bm-label">Phone numbers</label>
                <textarea className="bm-textarea" rows={4}
                  placeholder={columnType === 'name' ? '919876543210\n919876543211' : '919876543210, 919876543211\nor one per line'}
                  value={numbersText} onChange={(e) => setNumbersText(e.target.value)} />
                <p className="bm-hint">{columnType === 'name' ? 'One number per line — must match the order of names below.' : 'Enter 10-digit number or with country code (+91).'}</p>
              </div>
              {columnType === 'name' && (
                <div className="bm-field">
                  <label className="bm-label">Names (same order as numbers)</label>
                  <textarea className="bm-textarea" rows={4} placeholder={'Ravi Kumar\nPriya Sharma'} value={namesText} onChange={(e) => setNamesText(e.target.value)} />
                </div>
              )}
            </>
          ) : (
            <div className="bm-field">
              <label className="bm-label">Upload file</label>
              <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="bm-input" onChange={(e) => setFile(e.target.files[0])} />
              <p className="bm-hint">First column = phone numbers.{columnType === 'category' && ' Second column = category (e.g. Teacher, Parent, Student).'}{columnType === 'name' && ' Second column = name.'}</p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button onClick={handleProcessNumbers} disabled={processing} className="bm-btn bm-btn-dark">
              {processing ? <><Loader2 size={15} className="bm-spin" /> Processing...</> : <><Users size={15} /> Process numbers</>}
            </button>
            {processedContacts.length > 0 && (
              <button onClick={handleOrganizeNumbers} className="bm-btn bm-btn-outline" type="button"><ListFilter size={15} /> Organize numbers</button>
            )}
          </div>

          {processedContacts.length > 0 && (
            <div className="bm-recipients-summary">
              <span className="bm-recipients-count"><strong>{processedContacts.length}</strong> valid contact(s) processed</span>
              {confirmedRecipients
                ? <span style={{ fontSize: '13px', color: '#14803C', fontWeight: 600 }}>{confirmedRecipients.length} confirmed and ready</span>
                : <span style={{ fontSize: '13px', color: '#92400E' }}>Use "Organize numbers" to filter, or send all as-is</span>}
            </div>
          )}
        </div>

        {/* Confirmed recipients */}
        {confirmedRecipients && confirmedRecipients.length > 0 && (
          <div className="bm-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <p className="bm-section-title">Confirmed recipients ({confirmedRecipients.length})</p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={handleEditRecipients} className="bm-btn bm-btn-outline bm-btn-sm" type="button"><ListFilter size={13} /> Edit</button>
              </div>
            </div>
            <div className="bm-table-wrap">
              <table className="bm-table">
                <thead>
                  <tr>
                    <th>Phone</th>
                    {lastColumnType === 'name' && <th>Name</th>}
                    {lastColumnType === 'category' && <th>Category</th>}
                  </tr>
                </thead>
                <tbody>
                  {confirmedRecipients.map((r, i) => (
                    <tr key={i}>
                      <td>{r.phone}</td>
                      {lastColumnType === 'name' && <td>{r.name || '—'}</td>}
                      {lastColumnType === 'category' && <td>{r.category || '—'}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bm-notice bm-notice-green">Ready to message these {confirmedRecipients.length} contact(s). Continue below to compose your message.</div>
          </div>
        )}

        {/* Step 2 — Message */}
        <div className="bm-card">
          <div className="bm-card-head">
            <div className="bm-icon-badge green"><MessageSquare size={18} /></div>
            <div>
              <p className="bm-card-title">Compose message</p>
              <p className="bm-card-desc">Write a free-text message or send an approved WhatsApp template.</p>
            </div>
          </div>

          <div className="bm-toggle-group">
            <button onClick={() => setSendMode('text')} className={`bm-toggle ${sendMode === 'text' ? 'active' : ''}`} type="button">Free text</button>
            <button onClick={() => setSendMode('template')} className={`bm-toggle ${sendMode === 'template' ? 'active' : ''}`} type="button"><LayoutTemplate size={14} /> WhatsApp template</button>
          </div>

          {sendMode === 'text' ? (
            <>
              {templates.length > 0 && (
                <div className="bm-field">
                  <label className="bm-label">Use saved template (optional)</label>
                  <select className="bm-select" value={selectedTemplateId} onChange={(e) => handleTemplateSelect(e.target.value)}>
                    <option value="">— None, write custom message —</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              )}
              <div className="bm-field">
                <label className="bm-label">Message content <span className="bm-required">*</span></label>
                <textarea className="bm-textarea" rows={4} placeholder="Type your message here..." value={messageContent} onChange={(e) => setMessageContent(e.target.value)} />
              </div>
              <div className="bm-check-row">
                <input type="checkbox" checked={saveAsTemplate} onChange={(e) => setSaveAsTemplate(e.target.checked)} id="saveTemplate" />
                <label htmlFor="saveTemplate">Save this as a reusable template</label>
              </div>
              {saveAsTemplate && (
                <div className="bm-field" style={{ marginTop: '12px' }}>
                  <label className="bm-label">Template name</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input type="text" className="bm-input" placeholder="e.g. Festival Greeting" value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
                    <button onClick={handleSaveTemplate} className="bm-btn bm-btn-outline" type="button" style={{ whiteSpace: 'nowrap' }}>Save</button>
                  </div>
                </div>
              )}
              <div className="bm-notice bm-notice-amber">Messages sent to contacts outside an active session may require an approved WhatsApp template.</div>
            </>
          ) : (
            <>
              {waTemplates.length === 0 ? (
                <div className="bm-empty">
                  <div className="bm-empty-icon"><LayoutTemplate size={22} /></div>
                  <p className="bm-empty-title">No approved templates</p>
                  <p className="bm-empty-desc">Sync approved templates from Meta in Settings → WhatsApp Configuration.</p>
                </div>
              ) : (
                <>
                  <div className="bm-field">
                    <label className="bm-label">Select template <span className="bm-required">*</span></label>
                    <select className="bm-select" value={selectedWaTemplateId} onChange={(e) => handleWaTemplateSelect(e.target.value)}>
                      <option value="">— Choose a template —</option>
                      {waTemplates.map(t => <option key={t.id} value={t.id}>{t.template_name} ({t.language_code})</option>)}
                    </select>
                  </div>
                  {selectedWaTemplate && (
                    <>
                      <div className="bm-preview-box">{selectedWaTemplate.body_text}</div>
                      {variableMapping.map((v, i) => (
                        <div key={i} className="bm-field">
                          <label className="bm-label">Variable {'{{' + (i + 1) + '}}'} source</label>
                          <div className="bm-var-row">
                            <select className="bm-select" style={{ maxWidth: '210px' }} value={v.source} onChange={(e) => updateVariableMapping(i, 'source', e.target.value)}>
                              <option value="category">Contact's category</option>
                              <option value="name">Contact's name</option>
                              <option value="fixed">Fixed text (same for everyone)</option>
                            </select>
                            <input type="text" className="bm-input" style={{ maxWidth: '210px' }} placeholder={v.source === 'fixed' ? 'Enter text' : 'Fallback if empty'} value={v.fallback} onChange={(e) => updateVariableMapping(i, 'fallback', e.target.value)} />
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  <div className="bm-notice bm-notice-green">Template messages can reach contacts even if they've never messaged you before.</div>
                </>
              )}
            </>
          )}
        </div>

        {/* Flow for replies — optional */}
        <div className="bm-card optional">
          <div className="bm-card-head">
            <div className="bm-icon-badge"><Workflow size={18} /></div>
            <div>
              <p className="bm-card-title">Flow for replies <span className="bm-optional-tag">Optional</span></p>
              <p className="bm-card-desc">When a recipient replies to this campaign, this flow runs for them instead of your normal published flow.</p>
            </div>
          </div>
          <div className="bm-field">
            <label className="bm-label">Select or create a flow</label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <select className="bm-select" style={{ flex: 1, minWidth: '200px' }} value={selectedCampaignFlowId} onChange={(e) => handleSelectCampaignFlow(e.target.value)} disabled={settingCampaignFlow}>
                <option value="">— Choose a flow —</option>
                {eligibleFlows.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
              <button onClick={handleCreateNewFlow} className="bm-btn bm-btn-outline" type="button"><Plus size={15} /> New flow</button>
            </div>
            {eligibleFlows.length === 0 && <p className="bm-hint">No unpublished flows available. Create one, or unpublish an existing flow to use it here.</p>}
          </div>
          {selectedCampaignFlowId && (
            <div className="bm-flow-active"><CheckCircle size={14} /> This flow runs for campaign replies — it won't respond to any other incoming messages.</div>
          )}
        </div>

        {/* Step 3 — Send */}
        <div className="bm-card">
          <div className="bm-card-head">
            <div className="bm-icon-badge green"><Send size={18} /></div>
            <div>
              <p className="bm-card-title">Send campaign</p>
              <p className="bm-card-desc">Review is complete — start sending to your confirmed recipients.</p>
            </div>
          </div>
          <button onClick={handleSendNow} disabled={sending || activeRecipients.length === 0} className="bm-btn bm-btn-primary bm-btn-block">
            {sending ? <><Loader2 size={16} className="bm-spin" /> Starting...</> : <><Send size={16} /> Send now ({activeRecipients.length})</>}
          </button>
        </div>

        {/* Live status */}
        {campaignData && (
          <div className="bm-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <p className="bm-section-title">{campaignData.campaign.name}</p>
              {statusBadge(campaignData.campaign.status)}
            </div>
            <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 16px 0' }}>
              {campaignData.campaign.sent_count} sent · {campaignData.campaign.failed_count} failed · {campaignData.campaign.total_recipients} total
            </p>
            <div className="bm-table-wrap">
              <table className="bm-table">
                <thead><tr><th>Phone</th><th>Status</th><th>Error</th></tr></thead>
                <tbody>
                  {campaignData.recipients.map((r, i) => (
                    <tr key={i}>
                      <td>{r.phone}</td>
                      <td>{statusBadge(r.status)}</td>
                      <td style={{ color: '#DC2626', fontSize: '12px' }}>{r.error_message || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Past campaigns */}
        <div className="bm-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
            <p className="bm-section-title"><FileText size={17} /> Past campaigns</p>
            <button onClick={loadPastCampaigns} className="bm-btn bm-btn-outline bm-btn-sm" type="button"><RefreshCw size={13} /> Refresh</button>
          </div>

          {loadingPast ? (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <Loader2 size={26} className="bm-spin" style={{ color: '#1E3A5F' }} />
            </div>
          ) : pastCampaigns.length === 0 ? (
            <div className="bm-empty">
              <div className="bm-empty-icon"><MessageSquare size={22} /></div>
              <p className="bm-empty-title">No campaigns yet</p>
              <p className="bm-empty-desc">Your bulk campaigns will appear here.</p>
            </div>
          ) : (
            <div className="bm-table-wrap">
              <table className="bm-table">
                <thead>
                  <tr><th>Name</th><th>Status</th><th>Recipients</th><th>Sent</th><th>Failed</th><th>Date</th><th></th></tr>
                </thead>
                <tbody>
                  {pastCampaigns.map(c => (
                    <tr key={c.id}>
                      <td>{c.name || `Campaign #${c.id}`}</td>
                      <td>{statusBadge(c.status)}</td>
                      <td>{c.total_recipients}</td>
                      <td>{c.sent_count}</td>
                      <td>{c.failed_count}</td>
                      <td>{new Date(c.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button onClick={() => navigate(`/bulk-message/campaign/${c.id}`)} className="bm-btn bm-btn-outline bm-btn-sm">View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* WhatsApp API Setup Required Modal */}
      {showApiSetupModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '20px',
            maxWidth: '500px',
            width: '100%',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid #e2e8f0',
            animation: 'modalSlideIn 0.25s ease-out'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #1e3a5f, #0f243e)',
              padding: '26px 28px',
              color: 'white',
              position: 'relative'
            }}>
              <button
                onClick={() => setShowApiSetupModal(false)}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '50%',
                  color: 'white',
                  width: '32px',
                  height: '32px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>

              <div style={{
                width: '46px',
                height: '46px',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '14px'
              }}>
                <AlertTriangle size={24} color="#fcd34d" />
              </div>

              <h2 style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: '700' }}>
                WhatsApp API Setup Required
              </h2>
              <p style={{ margin: 0, fontSize: '13px', opacity: 0.85, lineHeight: '1.5' }}>
                Active Meta WhatsApp Business credentials are required to execute bulk broadcasts.
              </p>
            </div>

            <div style={{ padding: '24px 28px' }}>
              <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '22px'
              }}>
                <p style={{ margin: '0 0 8px', fontWeight: '700', fontSize: '13.5px', color: '#1e293b' }}>
                  Why is this needed?
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: '#475569', lineHeight: '1.6' }}>
                  To send bulk campaigns directly from your authorized WhatsApp Business number via Meta's official Cloud API, you need to configure your <strong>Phone Number ID</strong>, <strong>Access Token</strong>, and <strong>Verify Token</strong>.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowApiSetupModal(false)}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#475569',
                    fontSize: '13.5px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Dismiss
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowApiSetupModal(false);
                    navigate('/settings');
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    background: '#1e3a5f',
                    color: '#ffffff',
                    border: 'none',
                    fontSize: '13.5px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(30, 58, 95, 0.2)'
                  }}
                >
                  Configure WhatsApp API <ArrowRight size={15} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BulkMessage;